/* eslint-disable */
/**
 * @flow
 * */
import settings from 'config/constants';

import CacheService from 'core/services/storage/CacheService';

import exceptionDetector from './interceptors/exceptionDetector';
import refreshTokenDetector from "./interceptors/refreshTokenDetector";
import { currentProductId } from './currentProduct';

const NETWORK_SERVICE = settings.NETWORK_SERVICE;

const AUTO_RELOAD_GRACE_MS = 10 * 1000;

function getHardNavigationType() {
  try {
    // Navigation Timing L2
    const entries = typeof performance !== 'undefined'
      && typeof performance.getEntriesByType === 'function'
      && performance.getEntriesByType('navigation');
    // $FlowFixMe
    const type = entries && entries[0] && entries[0].type;
    if (type) return type;
  } catch (e) {
    // ignore
  }

  try {
    // Legacy fallback (deprecated but still widely supported)
    const legacyType = typeof performance !== 'undefined'
      && performance.navigation
      && performance.navigation.type;
    if (legacyType === 1) return 'reload';
    if (legacyType === 2) return 'back_forward';
    if (legacyType === 0) return 'navigate';
  } catch (e) {
    // ignore
  }

  return null;
}

const HARD_NAV_TYPE_AT_LOAD = getHardNavigationType();

function isRecentHardReload() {
  if (HARD_NAV_TYPE_AT_LOAD !== 'reload') return false;
  try {
    // `performance.now()` is ms since navigation start.
    return typeof performance !== 'undefined'
      && typeof performance.now === 'function'
      && performance.now() < AUTO_RELOAD_GRACE_MS;
  } catch (e) {
    return false;
  }
}

class NetworkService {
  _interceptors = [exceptionDetector, refreshTokenDetector];
  _uri: ?string = null;

  // $FlowFixMe
  constructor(uri: string, interceptors = []) {
    if (!uri || typeof uri !== 'string') {
      throw new Error('The "uri" argument must be string.');
    }

    if (interceptors.length) {
      interceptors.forEach(interceptor => {
        if (typeof interceptor !== 'function') {
          throw new Error(`The '${interceptor}' is not a function.`)
        }
        this._interceptors.push(interceptor)
      });
    }

    this._uri = uri;
  };

  makeAPIGetRequest = (url: string, options: Object = {}) => {
    options = options || {};
    options.method = NETWORK_SERVICE.REQUEST_METHODS.GET;
    return this.makeAPIRequest(url, options);
  };

  makeAPIPostRequest = (url: string, options: Object = {}) => {
    options.method = NETWORK_SERVICE.REQUEST_METHODS.POST;
    return this.makeAPIRequest(url, options);
  };

  makeAPIGeneralRequest = (urlPrefix: string, options: Object = {}, type:string) => {
    options.method = type;
    return this.makeAPIRequest(urlPrefix, options);
  };

  createUrl = (arg: Array<string> | string) => {
    if (Array.isArray(arg)) {
      return [this._uri, ...arg].join('/');
    }
    // $FlowFixMe
    return `${this._uri}/${arg}`;
  };

  createQueryParams = (queryParams: Object) => Object.keys(queryParams).reduce((accumulator, key) => {
    const item = queryParams[key];
    if (item === null || item === undefined) return accumulator;

    if (Array.isArray(item)) {
      for (let index = 0; index < item.length; index++) {
        const arrItem = item[index];
        accumulator += `${key}=${arrItem}&`;
      }
    } else {
      accumulator += `${key}=${item}&`;
    }
    return accumulator;
  }, '');

  // $FlowFixMe
  makeAPIRequest = (partUrl: string, options: ?Object = {}): Promise => {
    return new Promise((resolve, reject) => {
      let url: string = this.createUrl(partUrl);

      if (!url) {
        return reject(NETWORK_SERVICE.ERRORS.INVALID_REQUEST_PARAMS);
      }
      // $FlowFixMe
      if (options.query_params) {
        url += `?${this.createQueryParams(options.query_params)}`;
      }
      // $FlowFixMe
      if (!options.method) {
        // $FlowFixMe
        options.method = NETWORK_SERVICE.REQUEST_METHODS.GET;
      }

      const liveCasinoToken =CacheService.getItem('live_casino_token');
      const auth_token = CacheService.getItem('auth_token');

      const authKey = auth_token ? { 'Authentication': auth_token } : null;

      const fetch_options = {
        // $FlowFixMe
        method: options.method,
        // $FlowFixMe
        headers: {
          Accept: 'application/json, text/plain',
          'Content-Type': 'application/json',
          version: 1
        }
      };

      // $FlowFixMe
      if (options.credentials) {
        // $FlowFixMe
        fetch_options.credentials = options.credentials;
      };

      if (authKey){
        fetch_options.headers = {
          ...fetch_options.headers,
          ...authKey
        }
      }
      if (liveCasinoToken){
        fetch_options.headers = {
          ...fetch_options.headers,
          LiveCasino:  liveCasinoToken
        }
      }

      // Which product the user was in when the call was made. Null outside the product routes
      // (login, admin, 404), and overridable per request through options.headers below.
      const productId = currentProductId();

      if (productId !== null && productId !== undefined) {
        fetch_options.headers = {
          ...fetch_options.headers,
          Product: productId
        }
      }


      // $FlowFixMe
      if (options.headers) {
        // $FlowFixMe
        fetch_options.headers = {
          ...fetch_options.headers,
          ...options.headers
        };
        if (!fetch_options.headers['Content-Type']){
          delete fetch_options.headers['Content-Type'];
        }
      }
      try {
        // $FlowFixMe
        if (options.body) {
          // $FlowFixMe
          fetch_options.body = fetch_options.headers['Content-Type'] === 'application/json' ? JSON.stringify(options.body) : options.body;
        }
      } catch (ex) {
        return reject({ message: NETWORK_SERVICE.ERRORS.INVALID_REQUEST_PARAMS });
      }

      if (options.abortController) {
        // $FlowFixMe
        fetch_options.signal = options.abortController.signal;
      }
      if (options.caching){
        const callCaching = CacheService.callCaching(url);
        if (callCaching) {
          return resolve(callCaching);
        }
      }
      fetch(url, fetch_options)
        .then(async response => {

          if (!response) {
            return reject({ message: NETWORK_SERVICE.ERRORS.INVALID_RESPONSE_DATA });
          }

          const { headers } = response;

          try {
            const modifiedHeader = headers.get('Modified') || headers.get('Last-Modified');
            const existingModified = CacheService.getItem('Modified');
            if (modifiedHeader && !existingModified) {
              CacheService.setItem('Modified', modifiedHeader);
            } else if (modifiedHeader && modifiedHeader !== existingModified) {
              CacheService.setItem('Modified', modifiedHeader);
              CacheService.deleteItem('callCaching');
              // Don't trigger an extra auto-reload if the user just refreshed the page moments ago.
              if (!isRecentHardReload()) {
                window.location.reload();
              }
            }
          } catch (e) {
            // ignore header/storage access errors
          }

          let body = {};

          const contentType = headers.get('content-type');

          if (contentType && contentType.indexOf('application/json') !== -1) {
            body = await response.json();
          }

          body.status = response.status;

          try {
            if (this._interceptors.length) {
              this._interceptors.forEach(interceptor => {
                if (typeof interceptor === 'function') {
                  // $FlowFixMe
                  body = interceptor(body, headers);
                }
              });
            }
          } catch(e) {
            reject({ message: e.message, res: { body, headers } });
          }
          if (response.status >= 400) {
            return reject({ message: body.AlertMessage, res: { body, headers } });
          }

          if (options.caching){
            CacheService.setCallCaching(url, { body, headers });
          }
          return resolve({ body, headers });
        }).catch(err => reject(err));
    })
  }
}

export default NetworkService;
